import { extractJSendResponse } from "../jsend/extractJSendResponse.js";

type HttpVerb = "GET" | "POST" | "PUT" | "DELETE";

let started = 0;
let startListener: () => void = () => undefined;
let endListener: () => void = () => undefined;
let errorListener: (error: unknown) => void = () => undefined;

/**
 * Sets callback that is called when the AJAX loader
 * icon should be displayed.
 */
export const setStartListener = (fn: () => void) => {
  startListener = fn;
};

/**
 * Sets callback that is called when the AJAX loader
 * icon should be hidden.
 */
export const setEndListener = (fn: () => void) => {
  endListener = fn;
};

/**
 * Sets the callback that is called when an AJAX error occurs.
 */
export const setErrorListener = (fn: (error: unknown) => void) => {
  errorListener = fn;
};

/**
 * Runs fetch request to the given URL. Expects the response to
 * be a JSend response.
 */
export const requestJson = async <R, T>(url: string, method: HttpVerb, data: T): Promise<R> => {
  return executeFetch(async () => {
    if (method === "GET") {
      const response = await fetch(url, {
        method,
        headers: {
          "Accept": "application/json"
        }
      });
      return extractJSendResponse(await response.json());
    } else {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(data)
      });
      return extractJSendResponse(await response.json());
    }
  });
};

/**
 * Executes GET request to fetch JSON response.
 */
export const requestGetJson = async <R>(url: string): Promise<R> =>
  requestJson<R, undefined>(url, "GET", undefined);

/**
 * Executes POST request with JSON and returns the returned JSON response.
 */
export const requestPostJson = async <R, T>(url: string, data: T): Promise<R> =>
  requestJson<R, T>(url, "POST", data);

/**
 * Executes POST request with JSON and returns the returned JSON response.
 */
export const requestPutJson = async <R, T>(url: string, data: T): Promise<R> =>
  requestJson<R, T>(url, "PUT", data);

/**
 * Executes DELETE request to fetch JSON response.
 */
export const requestDeleteJson = async <R>(url: string): Promise<R> =>
  requestJson<R, undefined>(url, "DELETE", undefined);

export type Progress = {
  loaded: number;
  total: number;
};

export type Uploader = {
  cancel: () => void;
};

/**
 * Uploads files (FormData) to the given URL. Assumes that
 * the response is a JSend response. The request is always of type POST.
 */
export const uploadFiles = async <T>(
  url: string,
  body: FormData,
  setUploader?: (uploader: Uploader) => void,
  handleProgress?: (progress: Progress) => void
): Promise<T | undefined> => {
  return new Promise<T | undefined>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    let cancelled = false;

    if (setUploader) {
      setUploader({
        cancel: () => {
          cancelled = true;
          xhr.abort();
        }
      });
    }

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        if (handleProgress) {
          handleProgress({
            loaded: event.loaded,
            total: event.total
          });
        }
      }
    });

    xhr.upload.addEventListener("error", () => {
      reject(new Error("XHR upload error."));
    });

    xhr.upload.addEventListener("abort", () => {
      reject(new Error("XHR upload was aborted."));
    });

    xhr.addEventListener("readystatechange", () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          try {
            resolve(extractJSendResponse(JSON.parse(xhr.responseText)));
          } catch (err) {
            reject(err);
          }
        } else {
          if (cancelled) {
            resolve(undefined);
          } else {
            reject("XHR status code is not 200.");
          }
        }
      }
    });

    xhr.open("POST", url);

    xhr.setRequestHeader("Accept", "application/json");
    xhr.withCredentials = true;

    xhr.send(body);
  });
};

/**
 * Increases the count and calls the start callback
 * when the sequence of AJAX calls begins.
 */
const startFetch = () => {
  started++;
  if (started === 1) {
    startListener();
  }
};

/**
 * Decreases the count and calls the end callback
 * when the sequence of AJAX calls ends.
 */
const endFetch = () => {
  started--;
  if (started === 0) {
    endListener();
  }
};

/**
 * Helper to execute fetch queries.
 */
const executeFetch = async <T>(factory: () => Promise<T>) => {
  startFetch();
  try {
    return await factory();
  } catch (err) {
    errorListener(err);
    throw err;
  } finally {
    endFetch();
  }
};
